import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { FaArrowRight, FaSearch, FaArrowLeft } from "react-icons/fa";

import { supabase } from "../../supabaseClient";
import HeaderContainer from "../CrewDashboard/HeaderContainer";
import Driver from "../../images/Male.png";

const driversPerPage = 15; // 3 rows of 5 cards each
const DriverDetail = () => {
  const navigate = useNavigate(); // Use useNavigate instead of useHistory

  const [currentPage, setCurrentPage] = useState(1);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    const fetchDrivers = async () => {
      // Get current manager's UserID for filtering
      const currentManagerId = parseInt(sessionStorage.getItem("UserId"));
      console.log("Current Manager ID from session:", currentManagerId);
      
      if (!currentManagerId) {
        console.error("No manager ID found in session");
        setDrivers([]);
        return;
      }
      
      // Join driver_auth and driver_profiles tables, filter by manager
      const { data, error } = await supabase
        .from("driver_auth")
        .select(
          `
          id,
          email,
          is_active,
          email_verified,
          created_at,
          updated_at,
          driver_profiles!inner (
            id,
            first_name,
            last_name,
            phone,
            license_number,
            license_expiry,
            employment_status,
            hire_date,
            avatar_url,
            created_at,
            updated_at,
            manager_id
          )
        `
        )
        .eq("is_active", true)
        .eq("driver_profiles.manager_id", currentManagerId);
        
      console.log("Driver query result:", { data, error, currentManagerId });

      if (error) {
        console.error("Error fetching drivers:", error.message);
        return;
      }

      const driversWithImageUrls = await Promise.all(
        data.map(async (driverAuth) => {
          let signedUrl = "";
          const profile = driverAuth.driver_profiles[0];

          if (profile?.avatar_url) {
            // Extract the internal path from the full public URL
            const urlParts = profile.avatar_url.split("/driver-photos/");
            const filePath = urlParts[1]; // 'driver-photos/1749749606240.jfif'

            if (!filePath) {
              console.warn("Invalid avatar_url path:", profile.avatar_url);
            } else {
              const { data: signedData, error: signedError } =
                await supabase.storage
                  .from("driver-photos")
                  .createSignedUrl(filePath, 315360000);

              if (signedError) {
                console.error(
                  "Error generating signed URL:",
                  signedError.message
                );
              } else {
                signedUrl = signedData?.signedUrl || "";
              }
            }
          }

          return {
            // Map to match existing structure for compatibility
            DriverID: driverAuth.id,
            FirstName: profile?.first_name || "",
            LastName: profile?.last_name || "",
            Email: driverAuth.email,
            Phone: profile?.phone || "",
            LicenseNumber: profile?.license_number || "",
            LicenseExpiry: profile?.license_expiry || "",
            EmploymentStatus: profile?.employment_status || "",
            HireDate: profile?.hire_date || "",
            AvatarUrl: signedUrl,
            IsActive: driverAuth.is_active,
            EmailVerified: driverAuth.email_verified,
            CreatedAt: driverAuth.created_at,
            UpdatedAt: driverAuth.updated_at,
            // Add experience field for display
            experience: `${profile?.employment_status || "Active"} Driver`,
          };
        })
      );

      setDrivers(driversWithImageUrls);
    };

    fetchDrivers();
  }, []);

  // Pagination Logic
  const indexOfLastDriver = currentPage * driversPerPage;
  const indexOfFirstDriver = indexOfLastDriver - driversPerPage;
  const currentDrivers = drivers.slice(indexOfFirstDriver, indexOfLastDriver);
  const addNew = useCallback(() => {
    navigate("/driver-new");
  }, [navigate]);
  const onDetail = useCallback(
    (driver) => () => {
      navigate(`/driver-info/${driver.DriverID}`);
    },
    [navigate]
  );

  return (
    <div className="p-6 w-full">
      <HeaderContainer />
      {/* Top Section */}
      <div className="flex w-full items-center mt-4 mid-wrap-bar">
        <p className="font-semibold text-lg">My Drivers [{drivers.length}]</p>

        <div className="flex-1 flex justify-end items-center space-x-4">
          {/* Search Input */}
          <div className="search-input-wrapper">
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Search Flight..."
                className="search-input"
              />
              <FaSearch className="search-icon" />
            </div>
          </div>
        </div>
        <div className="flex items-center">
          <div
            onClick={addNew}
            className="justify-between p-2 pr-20 pl-20 rounded-lg flex items-center custom-button m-2"
          >
            <span>Add New</span>
          </div>

          {/* Dropdown */}
          <select className="custom-input border border-gray-300 rounded-lg py-2 px-4 focus:outline-none ml-1">
            <option>Air Crew Transport</option>
          </select>
        </div>
      </div>

      {/* Driver Info Grid */}

      {/* Pagination Controls */}
      <div>
        {/* Driver Cards Grid */}
        <div className="driver-grid">
          {currentDrivers.map((driver, index) => (
            <div key={driver.DriverID} className="driver-card">
              <div>
                <img
                  src={driver.AvatarUrl || Driver} // Fallback if not found
                  alt="Driver"
                  className="driver-img"
                />
                <h2 className="driver-name">
                  {`${driver.FirstName} ${driver.LastName}`}
                </h2>
                <p className="driver-experience">{driver.experience}</p>
              </div>

              {/* More Info Button */}
              <div onClick={onDetail(driver)} className="more-info-button">
                <span>More Info</span>
                <FaArrowRight />
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="pagination-container">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            <FaArrowLeft className="pagination-icon" />
          </button>

          {Array.from(
            { length: Math.ceil(drivers.length / driversPerPage) },
            (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`pagination-button ${currentPage === i + 1 ? "active" : ""
                  }`}
              >
                {i + 1}
              </button>
            )
          )}

          <button
            onClick={() =>
              setCurrentPage((prev) =>
                Math.min(prev + 1, Math.ceil(drivers.length / driversPerPage))
              )
            }
            disabled={indexOfLastDriver >= drivers.length}
            className="pagination-button"
          >
            <FaArrowRight className="pagination-icon" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverDetail;
